import { designVariables } from "@studio3t/design-variables";
import { BadgeNeutralSubtle, Tooltip } from "@studio3t/generic-components";
import type { ReactNode } from "react";
import styled from "styled-components";

const SINGLE_LINE = designVariables.type.singleLine;

// Hover tooltip for a "+ N" overflow counter: lists the hidden items as
// neutral-subtle badges under an uppercase heading (e.g. "Sink", "Middleware",
// "Pipelines"). Matches Figma 13822-36551. The trigger is passed as children.
export function MoreTooltip({
  heading,
  items,
  children,
}: {
  heading: string;
  items: string[];
  children: ReactNode;
}) {
  return (
    <Tooltip
      side="top"
      type="multiline"
      tooltipWrapperProps={{ display: "inline-flex" }}
      text={
        <Section>
          <Heading>{heading}</Heading>
          <Items>
            {items.map((label) => (
              <Badge key={label}>
                <BadgeLabel title={label}>{label}</BadgeLabel>
              </Badge>
            ))}
          </Items>
        </Section>
      }
    >
      {children}
    </Tooltip>
  );
}

// Fixed width keeps the tooltip consistent regardless of name length (matches
// the table's filter-dropdown width); badges that don't fit ellipsize.
// tooltip/gap (6) between the heading and the list; tooltip/gaps (2) between
// badges — small literals; design-variables doesn't export tooltip dimensions.
const TOOLTIP_WIDTH = 240;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: ${TOOLTIP_WIDTH}px;
`;

const Heading = styled.span`
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  text-transform: uppercase;
  /* Figma Tooltip/text — white on the dark tooltip background. */
  color: ${(props) => props.theme.tooltipText};
`;

// Badges flow left-to-right and wrap onto a new row when the (fixed-width) row
// runs out of space.
const Items = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
`;

// Cap each badge to the tooltip width so a long name ellipsizes instead of
// widening the tooltip.
const Badge = styled(BadgeNeutralSubtle)`
  max-width: 100%;
`;

const BadgeLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
